import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import {Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {take} from "rxjs";
import {ASSET_MANAGEMENT_URL} from "../../../app.config";
import {API_ENDPOINTS, APP_CONSTANTS} from "../../../app.constants";
import {ToastSeverityEnum} from "../../../enums";
import {prepareFiltersForParams} from "../../../helpers";
import {
  AssetTypeModel,
  DtoAssetTypeResponseInterface,
  DtoAssetTypeResponseResultsInterface,
  DtoMediaUploadInfoInterface,
  FilterInterface,
  MediaInfoModel
} from "../../../interfaces";
import {mapAssetTypeFromDto, mapAssetTypeFromDtoArray, mapAssetTypeToDto} from "../../../mapping";
import {
  addAssetType,
  addAssetTypes,
  setAssetTypeQueryItems,
  setAssetTypes,
  setAssetTypesPaginationInfo,
  setPaginatedAssetTypes,
  updateAssetType
} from "../../../store/actions/asset-types.actions";
import {selectAssetTypeFilters, selectAssetTypeQuery} from "../../../store/reducers/asset-types.reducer";
import {ToastsService} from "../../app-services";

@Injectable({
  providedIn: "root"
})
export class AssetTypeService {
  private readonly httpClient = inject(HttpClient);
  private readonly toastsService = inject(ToastsService);
  private readonly router = inject(Router);
  private readonly assetManagementUrl = inject(ASSET_MANAGEMENT_URL);
  private readonly store = inject(Store);

  private _timer?: number;
  private _debounce = false;

  constructor() {
    this.store.select(selectAssetTypeFilters).subscribe(() => {
      if (this._debounce) {
        this._timer = window.setTimeout(() => {
          if (this._timer) {
            clearTimeout(this._timer);
          }
          this.getAssetTypesPaginated();
        }, APP_CONSTANTS.DEBOUNCE_TIME);
      } else {
        this.getAssetTypesPaginated();
      }
    });

    this.store.select(selectAssetTypeQuery).subscribe((query: string) => {
      if (query && query !== "") {
        this.queryAssetTypes(query);
      }
    });
  }

  public getAssetType(id: string): void {
    const idEncoded = encodeURIComponent(id);
    this.httpClient
      .get<DtoAssetTypeResponseInterface>(this.assetManagementUrl + API_ENDPOINTS.ASSET_TYPES + `/${idEncoded}`)
      .subscribe({
        next: data => {
          this.store.dispatch(updateAssetType({assetType: mapAssetTypeFromDto(data)}));
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.toastsService.addToast({
              severity: ToastSeverityEnum.Error,
              message: `Asset type "${id}" not found. Redirecting to overview.`
            });
            this.router.navigate(["/asset-management/asset-type"]);
          } else {
            this.toastsService.addToast({severity: ToastSeverityEnum.Error, message: error.message});
          }
        }
      });
  }

  public getAssetTypesPaginated() {
    this.store
      .select(selectAssetTypeFilters)
      .pipe(take(1))
      .subscribe((filters: FilterInterface) => {
        const queryParams = prepareFiltersForParams(filters);
        this.httpClient
          .get<DtoAssetTypeResponseResultsInterface>(this.assetManagementUrl + API_ENDPOINTS.ASSET_TYPES, {params: queryParams})
          .subscribe({
            next: data => {
              this.store.dispatch(setPaginatedAssetTypes({assetTypes: mapAssetTypeFromDtoArray(data.results ?? [])}));
              this.store.dispatch(
                setAssetTypesPaginationInfo({
                  paginationInfo: {
                    from: (data.offset ?? 0) + 1,
                    to: (data.offset ?? 0) + (data.count ?? 0),
                    totalResults: data.totalCount ?? 0,
                    currentPage: (data.offset ?? 0) / APP_CONSTANTS.PAGINATION_PAGE_SIZE + 1,
                    totalPages: Math.ceil((data.totalCount ?? 0) / APP_CONSTANTS.PAGINATION_PAGE_SIZE)
                  }
                })
              );
            },
            error: (error: HttpErrorResponse) => {
              this.toastsService.addToast({
                severity: ToastSeverityEnum.Error,
                message: "Error fetching AssetTypes Paginated, Status " + error.status
              });
            }
          });
      });
  }

  public getAllAssetTypes() {
    this.store.dispatch(setAssetTypes({assetTypes: []}));
    this.getAssetTypesRecursive(0);
  }

  private getAssetTypesRecursive(offset: number) {
    this.httpClient
      .get<DtoAssetTypeResponseResultsInterface>(this.assetManagementUrl + API_ENDPOINTS.ASSET_TYPES, {params: {offset: offset}})
      .subscribe({
        next: data => {
          this.store.dispatch(addAssetTypes({assetTypes: mapAssetTypeFromDtoArray(data.results ?? [])}));
          if (data.next) {
            setTimeout(() => {
              this.getAssetTypesRecursive(offset + APP_CONSTANTS.MAX_API_RESULTS_LIMIT);
            }, 100);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: "Error fetching AssetTypes, Status " + error.status
          });
        }
      });
  }

  private queryAssetTypes(query: string) {
    this.httpClient
      .get<DtoAssetTypeResponseResultsInterface>(this.assetManagementUrl + API_ENDPOINTS.ASSET_TYPES, {
        params: {search: query, limit: APP_CONSTANTS.QUERY_PAGE_SIZE}
      })
      .subscribe({
        next: data => {
          this.store.dispatch(setAssetTypeQueryItems({items: mapAssetTypeFromDtoArray(data.results ?? [])}));
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: "Error querying AssetTypes, Status " + error.status
          });
        }
      });
  }

  public updateAssetType(model: AssetTypeModel, files: Array<File> | null = null): void {
    const assetTypeEncoded = encodeURIComponent(model.assetType ?? "");
    this.httpClient
      .post(this.assetManagementUrl + API_ENDPOINTS.ASSET_TYPES + `/${assetTypeEncoded}`, mapAssetTypeToDto(model))
      .subscribe({
        complete: () => {
          this.store.dispatch(updateAssetType({assetType: model}));
          this.getAssetTypesPaginated();
          this.getAssetType(model.assetType ?? "");
          this.toastsService.addToast({severity: ToastSeverityEnum.Success, message: "Custom Asset Type updated"});

          if (files) {
            this.uploadImages(files, model.assetType ?? "");
          }
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to update AssetType ${model.assetType}, Status ${error.status}`
          });
        }
      });
  }

  public createAssetType(model: AssetTypeModel, files: Array<File> | null = null): void {
    const assetTypeEncoded = encodeURIComponent(model.assetType ?? "");
    this.httpClient
      .put(this.assetManagementUrl + API_ENDPOINTS.ASSET_TYPES + `/${assetTypeEncoded}`, mapAssetTypeToDto(model))
      .subscribe({
        complete: () => {
          this.store.dispatch(addAssetType({assetType: model}));
          this.getAssetTypesPaginated();
          this.toastsService.addToast({severity: ToastSeverityEnum.Success, message: "New Custom Asset Type added"});

          if (files) {
            this.uploadImages(files, model.assetType ?? "");
          }
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to create AssetType ${model.assetType}, Status ${error.status}`
          });
        }
      });
  }

  public deleteAssetType(assetTypeId: string): void {
    const assetTypeIdEncoded = encodeURIComponent(assetTypeId);
    this.httpClient.delete(this.assetManagementUrl + API_ENDPOINTS.ASSET_TYPES + `/${assetTypeIdEncoded}`).subscribe({
      next: () => {
        this.getAssetTypesPaginated();
        this.toastsService.addToast({severity: ToastSeverityEnum.Success, message: `Asset Type ${assetTypeId} deleted`});
      },
      error: (error: HttpErrorResponse) => {
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to delete Asset Type ${assetTypeId}, Status ${error.status}`
        });
      }
    });
  }

  public uploadImages(files: Array<File>, assetType: string) {
    const assetTypeEncoded = encodeURIComponent(assetType);
    files.forEach(file => {
      const formData = new FormData();
      formData.append("file", file);
      this.httpClient
        .post<DtoMediaUploadInfoInterface>(
          this.assetManagementUrl + API_ENDPOINTS.ASSET_TYPES + `/${assetTypeEncoded}/media/images`,
          formData
        )
        .subscribe({
          complete: () => {
            this.getAssetType(assetType);
            this.toastsService.addToast({severity: ToastSeverityEnum.Success, message: `Uploaded Image`});
          },
          error: (error: HttpErrorResponse) => {
            this.toastsService.addToast({
              severity: ToastSeverityEnum.Error,
              message: `Failed to upload Image, Status ${error.status}`
            });
          }
        });
    });
  }

  public deleteImage(assetType: string, image: MediaInfoModel): void {
    const assetTypeEncoded = encodeURIComponent(assetType);
    const imageNameEncoded = encodeURIComponent(image.name ?? "");
    this.httpClient
      .delete(this.assetManagementUrl + API_ENDPOINTS.ASSET_TYPES + `/${assetTypeEncoded}/media/images/${imageNameEncoded}`)
      .subscribe({
        complete: () => {
          this.getAssetType(assetType);
          this.toastsService.addToast({severity: ToastSeverityEnum.Success, message: "Deleted Image"});
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to delete Image, Status ${error.status}`
          });
        }
      });
  }
}
